/**
 * 運費明細 / 運費歸單的前端 API(REQ-20260908002)。
 *
 * 放在自己的 feature 資料夾而不是 `features/accounting/api.ts`:端點自成一組
 * (`/freight-lines`),而且出貨端(`useCreateShipment`)也要失效它的 cache ——
 * query key 只有 `['freight-lines', ...]` 一個字首,兩邊對得起來。
 */
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type {
  ApiSuccess,
  ConsolidateFreightLinesInput,
  ConsolidateFreightLinesResponse,
  FreightLineItem,
  FreightLineListResponse,
  PatchFreightLineInput,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

export interface FreightLineFilter {
  vendorId: number | undefined;
  from: string;
  to: string;
}

/**
 * 某廠商某段運送日期的**未歸單**明細。
 * 沒選廠商時 `enabled: false` —— 一次請求都不發(選廠商是這個畫面的第一步)。
 */
export function useFreightLines(
  filter: FreightLineFilter,
): ReturnType<typeof useQuery<FreightLineListResponse>> {
  return useQuery({
    queryKey: ['freight-lines', filter],
    enabled: filter.vendorId != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<FreightLineListResponse>>('/freight-lines', {
        params: { vendorId: filter.vendorId, from: filter.from, to: filter.to },
      });
      return res.data.data;
    },
  });
}

/**
 * 建立歸單的進貨單。建出來的是 **DRAFT** —— 之後仍走既有的送出 / 確認,
 * 所以這裡也要失效 `purchase-orders`(進貨管理列表會多一張草稿)。
 * 應付那幾組**不失效**:草稿還沒過帳,一毛應付都還沒產生。
 */
export function useConsolidateFreightLines(): UseMutationResult<
  ConsolidateFreightLinesResponse,
  Error,
  ConsolidateFreightLinesInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ConsolidateFreightLinesInput) => {
      const res = await apiClient.post<ApiSuccess<ConsolidateFreightLinesResponse>>(
        '/freight-lines/consolidate',
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['freight-lines'] });
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

/** 補託運單號 / 金額(只有未歸單的改得動,後端擋) */
export function usePatchFreightLine(): UseMutationResult<
  FreightLineItem,
  Error,
  { id: number } & PatchFreightLineInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => {
      const res = await apiClient.patch<ApiSuccess<FreightLineItem>>(
        `/freight-lines/${id}`,
        body,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['freight-lines'] });
    },
  });
}
